import React, { useEffect, useRef, useState } from 'react';
import {
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  SearchableSelect,
  Tag,
} from '@jem-open/jem-ui';
import axios from 'axios';
import {
  computeInvoiceGroups,
  invoiceGroupLabel,
  InvoiceStrategy,
  RECON_PRODUCTS,
  ReconProductValue,
  useMultiProductRecon,
} from '../MultiProductReconContext';
import { environment } from '../../../../../environment/environment';

interface XeroContactRow {
  ContactID: string;
  Name: string;
}

const XeroContactPicker = () => {
  const { state, setXeroContact } = useMultiProductRecon();
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState<XeroContactRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Monotonic id of the most recent search. Responses from superseded
  // requests are ignored so a slow earlier response can't clobber the
  // results of a newer search (out-of-order response race).
  const latestRequestRef = useRef(0);

  const searchContacts = async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    const isStale = () => latestRequestRef.current !== requestId;

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<XeroContactRow[]>(
        `${environment.baseUrl}/xero_config/get_xero_contacts?searchTerm=${encodeURIComponent(
          searchTerm,
        )}`,
      );
      if (isStale()) return;
      setContacts(res.data ?? []);
    } catch {
      if (isStale()) return;
      setError('Could not load Xero contacts. Try again.');
      setContacts([]);
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  const options = contacts.map((c) => ({ value: c.ContactID, label: c.Name }));

  return (
    <div className="space-y-2 rounded-md border bg-white p-3">
      <Label className="font-semibold !leading-none">
        Xero billing contact <span className="text-red-600">*</span>
      </Label>
      <p className="text-xs text-muted-foreground">
        The Xero contact this schedule&apos;s invoices are raised against.
        Search (min 3 characters), then select.
      </p>
      <div className="flex items-center gap-2">
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Xero contacts (min 3 characters)"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && searchTerm.length >= 3) {
              e.preventDefault();
              searchContacts();
            }
          }}
        />
        <button
          type="button"
          disabled={searchTerm.length < 3 || loading}
          onClick={searchContacts}
          className="whitespace-nowrap rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {contacts.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {contacts.length} result{contacts.length === 1 ? '' : 's'} — select
          one:
        </p>
      )}
      <SearchableSelect
        options={options}
        value={state.xeroContactId ?? ''}
        onValueChange={(value) => {
          const match = options.find((o) => o.value === value);
          setXeroContact(value || null, match ? match.label : null);
        }}
        placeholder={
          state.xeroContactName ?? 'No contact selected — search above first'
        }
        searchPlaceholder="Filter results…"
        emptyMessage="No contacts — search above"
        disabled={contacts.length === 0}
      />
      {!state.xeroContactId && (
        <p className="text-xs text-amber-600">A Xero contact is required.</p>
      )}
    </div>
  );
};

const ResolvedInvoiceMapping = () => {
  const { state } = useMultiProductRecon();
  const groups = computeInvoiceGroups(
    state.recons,
    state.invoiceStrategy,
    state.customInvoiceGroupByProduct,
  );

  if (groups.length === 0) return null;

  return (
    <div
      className="space-y-1 rounded-md border bg-white p-3"
      aria-live="polite"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Resolved invoices ({groups.length})
      </p>
      <ul className="space-y-1">
        {groups.map((g) => (
          <li
            key={g.groupNumber}
            className="flex flex-wrap items-center gap-2 text-sm"
          >
            <Tag variant="outline-navy">Invoice {g.groupNumber}</Tag>
            <span className="text-[--greyscale-text-body]">
              {invoiceGroupLabel(g, state.recons, state.invoiceStrategy)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const InvoicingSection = () => {
  const {
    state,
    setInvoiceStrategy,
    setProductInvoiceGroup,
    setInvoiceReference,
  } = useMultiProductRecon();

  const selectedProducts: ReconProductValue[] = RECON_PRODUCTS.filter((rp) =>
    state.recons.some((r) => r.products.includes(rp.value)),
  ).map((rp) => rp.value);
  const productCount = selectedProducts.length;

  // Custom only makes sense when there are 2+ products to split between
  // invoices; fall back to combined if the user removes products.
  useEffect(() => {
    if (productCount < 2 && state.invoiceStrategy === 'custom') {
      setInvoiceStrategy('combined');
    }
  }, [productCount, state.invoiceStrategy, setInvoiceStrategy]);

  const invoiceGroupOptions = Array.from(
    { length: Math.max(productCount, 1) },
    (_, i) => ({
      value: String(i + 1),
      label: `Invoice ${i + 1}`,
    }),
  );

  const productLabelFor = (p: ReconProductValue): string =>
    RECON_PRODUCTS.find((rp) => rp.value === p)?.label ?? p;

  return (
    <div className="space-y-2">
      <XeroContactPicker />
      <div className="space-y-1 rounded-md border bg-white p-3">
        <Label
          htmlFor="mp-invoice-reference"
          className="font-semibold !leading-none"
        >
          Invoice reference{' '}
          <span className="text-muted-foreground">(optional)</span>
        </Label>
        <p className="text-xs text-muted-foreground">
          Overrides the default &quot;Employee Benefits {'{month}'} {'{year}'}
          &quot; reference on every invoice for this schedule.
        </p>
        <Input
          id="mp-invoice-reference"
          value={state.invoiceReference}
          onChange={(e) => setInvoiceReference(e.target.value)}
          placeholder="e.g. Employee Benefits April 2026"
        />
      </div>
      <p className="text-sm text-muted-foreground">
        How should invoices be generated for these recons?
      </p>
      <RadioGroup
        value={state.invoiceStrategy}
        onValueChange={(value) =>
          setInvoiceStrategy(value as InvoiceStrategy)
        }
        className="space-y-2 pt-1"
      >
        <div className="flex items-start gap-3 rounded-md border bg-white p-3">
          <RadioGroupItem
            value="separate"
            id="mp-invoice-separate"
            className="mt-1"
          />
          <div className="flex-1 space-y-0.5">
            <Label
              htmlFor="mp-invoice-separate"
              className="font-semibold !leading-none"
            >
              Separate invoice per recon
            </Label>
            <p className="text-xs text-muted-foreground">
              Each recon produces its own invoice. Default for the standard
              workflow.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-md border bg-white p-3">
          <RadioGroupItem
            value="perProduct"
            id="mp-invoice-per-product"
            className="mt-1"
          />
          <div className="flex-1 space-y-0.5">
            <Label
              htmlFor="mp-invoice-per-product"
              className="font-semibold !leading-none"
            >
              Separate invoice per product
            </Label>
            <p className="text-xs text-muted-foreground">
              Each product gets its own invoice, even when products share a
              recon.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-md border bg-white p-3">
          <RadioGroupItem
            value="combined"
            id="mp-invoice-combined"
            className="mt-1"
          />
          <div className="flex-1 space-y-0.5">
            <Label
              htmlFor="mp-invoice-combined"
              className="font-semibold !leading-none"
            >
              One combined invoice
            </Label>
            <p className="text-xs text-muted-foreground">
              All recons roll into a single invoice for the employer.
            </p>
          </div>
        </div>
        {productCount > 1 && (
          <div className="flex items-start gap-3 rounded-md border bg-white p-3">
            <RadioGroupItem
              value="custom"
              id="mp-invoice-custom"
              className="mt-1"
            />
            <div className="flex-1 space-y-0.5">
              <Label
                htmlFor="mp-invoice-custom"
                className="font-semibold !leading-none"
              >
                Custom grouping
              </Label>
              <p className="text-xs text-muted-foreground">
                Pick which products share an invoice. Products assigned the
                same group are combined onto one invoice.
              </p>
            </div>
          </div>
        )}
      </RadioGroup>

      {state.invoiceStrategy === 'custom' && productCount > 1 && (
        <div className="mt-3 space-y-2 rounded-md border bg-slate-50 p-3">
          <p className="text-xs text-muted-foreground">
            Assign each product to an invoice group:
          </p>
          <div className="space-y-2">
            {selectedProducts.map((product) => (
              <div
                key={product}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-white p-2 pl-3"
              >
                <span className="text-sm font-medium">
                  {productLabelFor(product)}
                </span>
                <div className="w-40">
                  <SearchableSelect
                    options={invoiceGroupOptions}
                    value={String(
                      state.customInvoiceGroupByProduct[product] ?? 1,
                    )}
                    onValueChange={(value) =>
                      setProductInvoiceGroup(
                        product,
                        value ? Number(value) : 1,
                      )
                    }
                    placeholder="Invoice group"
                    searchPlaceholder="Search..."
                    emptyMessage="No match"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="pt-2">
        <ResolvedInvoiceMapping />
      </div>
    </div>
  );
};

export default InvoicingSection;
